<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue';

const props = defineProps({
	text: { type: [String, Function], required: true },
	placement: {
		type: String,
		default: null
	},
	position: {
		type: String,
		default: 'top',
		validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v)
	},
	disabled: { type: Boolean, default: false },
	beforeShow: { type: Function, default: null }
});
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const show = ref(false);
const resolvedText = ref('');
const preferredPosition = computed(() => props.placement ?? props.position);
const resolvedPlacement = ref(preferredPosition.value);
const top = ref(0);
const left = ref(0);

const trigger = ref(null);
const tooltip = ref(null);

const inlineStyle = computed(() => ({ top: `${top.value}px`, left: `${left.value}px` }));

async function handleMouseEnter() {
	if (props.disabled) return;
	// 1) let caller run side effects and optionally return text
	let textFromBefore = null;
	if (props.beforeShow) {
		try {
			textFromBefore = await props.beforeShow();
		} catch (e) {
			console.error('beforeShow', e);
		}
	}

	// 2) Resolve tooltip text (prefer the beforeShow return if provided)
	if (typeof props.text === 'function') {
		try {
			resolvedText.value = await props.text();
		} catch {
			resolvedText.value = 'Error loading tooltip text';
		}
	} else {
		resolvedText.value = textFromBefore ?? props.text;
	}

	show.value = !!resolvedText.value;
	await nextTick();
	updatePosition();
}

function handleMouseLeave() {
	show.value = false;
}

function updatePosition() {
	if (!show.value || !trigger.value || !tooltip.value) return;

	const tRect = trigger.value.getBoundingClientRect();
	const { width: tw, height: th } = tooltip.value.getBoundingClientRect();
	const gap = 8;

	const fitsTop = tRect.top - gap - th >= 0;
	const fitsBottom = tRect.bottom + gap + th <= window.innerHeight;
	const fitsLeft = tRect.left - gap - tw >= 0;
	const fitsRight = tRect.right + gap + tw <= window.innerWidth;

	let place = preferredPosition.value;
	if (place === 'top') place = fitsTop ? 'top' : fitsBottom ? 'bottom' : fitsRight ? 'right' : 'left';
	else if (place === 'bottom') place = fitsBottom ? 'bottom' : fitsTop ? 'top' : fitsRight ? 'right' : 'left';
	else if (place === 'left') place = fitsLeft ? 'left' : fitsRight ? 'right' : fitsTop ? 'top' : 'bottom';
	else if (place === 'right') place = fitsRight ? 'right' : fitsLeft ? 'left' : fitsTop ? 'top' : 'bottom';

	let yy = 0,
		xx = 0;
	if (place === 'top') {
		yy = tRect.top - th - gap;
		xx = tRect.left + tRect.width / 2 - tw / 2;
	}
	if (place === 'bottom') {
		yy = tRect.bottom + gap;
		xx = tRect.left + tRect.width / 2 - tw / 2;
	}
	if (place === 'left') {
		yy = tRect.top + tRect.height / 2 - th / 2;
		xx = tRect.left - tw - gap;
	}
	if (place === 'right') {
		yy = tRect.top + tRect.height / 2 - th / 2;
		xx = tRect.right + gap;
	}

	// clamp to viewport
	xx = Math.max(8, Math.min(xx, window.innerWidth - tw - 8));
	yy = Math.max(8, Math.min(yy, window.innerHeight - th - 8));

	resolvedPlacement.value = place;
	top.value = Math.round(yy);
	left.value = Math.round(xx);
}

const onScroll = () => updatePosition();
const onResize = () => updatePosition();

onMounted(() => {
	window.addEventListener('scroll', onScroll, true);
	window.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
	window.removeEventListener('scroll', onScroll, true);
	window.removeEventListener('resize', onResize);
});
</script>
<template>
	<div>
		<span ref="trigger" class="inline-block w-full" v-bind="attrs" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<slot />
		</span>
		<teleport to="body">
			<div
				v-if="show && resolvedText"
				ref="tooltip"
				class="egs-tooltip fixed z-[10000] bg-gray-800 text-white py-1.5 px-2.5 rounded max-w-[250px] w-max leading-relaxed text-sm opacity-95 pointer-events-none transition-opacity duration-150"
				:class="'egs-tooltip--' + resolvedPlacement"
				:style="inlineStyle"
			>
				{{ resolvedText }}
			</div>
		</teleport>
	</div>
</template>
